import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import TreeView from '@mui/lab/TreeView';
import SvgIcon from '@mui/material/SvgIcon';
import { alpha, styled } from '@mui/material/styles';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import Collapse from '@mui/material/Collapse';
import {Box, Divider} from "@mui/material";
import { useSpring, animated } from 'react-spring';
import {useContext} from "react";
import {DataContext} from "../contexts/DataContext";
import {formatDateFlood, extractDateParts} from "../utils/dateFunctions";

function MinusSquare(props) {
    return (
        <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
        </SvgIcon>
    );
}

function PlusSquare(props) {
    return (
        <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
        </SvgIcon>
    );
}

function TransitionComponent(props) {
    const style = useSpring({
        from: {
            opacity: 0,
            transform: 'translate3d(20px,0,0)',
        },
        to: {
            opacity: props.in ? 1 : 0,
            transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
        },
    });

    return (
        <animated.div style={style}>
            <Collapse {...props} />
        </animated.div>
    );
}

TransitionComponent.propTypes = {
    /**
     * Show the component; triggers the enter or exit states
     */
    in: PropTypes.bool,
};

const StyledTreeItem = styled((props) => (
    <TreeItem {...props} TransitionComponent={TransitionComponent} />
))(({ theme }) => ({
    [`& .${treeItemClasses.iconContainer}`]: {
        '& .close': {
            opacity: 0.3,
        },
    },
    [`& .${treeItemClasses.group}`]: {
        marginLeft: 15,
        paddingLeft: 18,
        borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
    },
}));

export default function FloodsTreeView(props) {

    const {countryData} = props

    const { t } = useTranslation();

    const {
        updateSelectedCountry,
        updateSelectedFlood,
    } = useContext(DataContext);

    const handleNodeSelect = (event, nodeId) => {
        const countryIndex = Math.floor(nodeId / 10000) - 1;
        const yearIndex = Math.floor((nodeId % 10000) / 100) - 1;
        const floodIndex = (nodeId % 100) - 1;

        if (countryIndex >= 0 && yearIndex >= 0 && floodIndex >= 0) {
            const country = Object.keys(countryData)[countryIndex];
            const year = Object.keys(countryData[country].year_by_year).reverse()[yearIndex];
            const flood = Object.keys(countryData[country].year_by_year[year]).reverse()[floodIndex];
            const floodPath = countryData[country].year_by_year[year][flood].path;

            updateSelectedCountry(country);
            console.log('Selected country: ' + country);
            // Handle the flood path as needed
            updateSelectedFlood(floodPath);
            console.log(floodPath);
        }
    };

    return (
        <Box sx={{ minWidth: 360, boxShadow: 20, marginLeft:2, maxWidth: 400}}>
            <TreeView
                aria-label="file system navigator"
                defaultCollapseIcon={<MinusSquare />}
                defaultExpandIcon={<PlusSquare />}
                sx={{ height: 240, flexGrow: 1, maxWidth: 400, overflowY: 'auto', backgroundColor: 'white' }}
                onNodeSelect={handleNodeSelect}
            >
                {Object.keys(countryData).map((country, id_country) => {
                    return <div className={'tree-item'}>
                        <div>
                            <StyledTreeItem nodeId={(id_country + 1) * 10000} label={t(`constants.countries.${country}`)}>
                                {Object.keys(countryData[country].year_by_year).length > 0 &&
                                    Object.keys(countryData[country].year_by_year).reverse().map((year, id_year) => {
                                        const label = year
                                        return <div className={'tree-item'}>
                                            <StyledTreeItem nodeId={(id_country + 1) * 10000 + (id_year + 1) * 100} label={label} sx={{whiteSpace: 'pre', textAlign: 'justify'}}>
                                            {Object.keys(countryData[country].year_by_year[year]).length > 0 &&
                                                Object.keys(countryData[country].year_by_year[year]).reverse().map((flood, id_flood) => {
                                                    const [, month, day] = extractDateParts(flood);
                                                    return <div className={'tree-item'}>
                                                        <StyledTreeItem nodeId={(id_country + 1) * 10000 + (id_year + 1) * 100 + id_flood + 1} label={`${t(`constants.months.${month - 1}`)}, ${day}`} sx={{whiteSpace: 'pre', textAlign: 'justify'}}/>
                                                    </div>
                                            }
                                            )}
                                            </StyledTreeItem>
                                        </div>
                                    })}
                            </StyledTreeItem>
                        </div>
                    </div>
                })}
            </TreeView>
        </Box>
    );
}
